import React, {
  useEffect, useLayoutEffect, useRef, useState, useCallback
} from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Pagination from 'react-js-pagination';
import Scrollbar from 'react-scrollbars-custom';
import CircularProgress from '@material-ui/core/CircularProgress';
import { makeStyles } from '@material-ui/core/styles';
import Row from './Row';
import RowSearch from './Search';
import styles from './DTM.module.scss';
import CogwheelIcon from '../../Icons/CogwheelIcon';
import CheckboxGroupRaw from '../CheckboxGroupRaw/CheckboxGroupRaw';
import ExcelIcon from '../../Icons/ExcelIcon';
import PdfIcon from '../../Icons/PdfIcon';
import StyledCheckbox from '../Checkbox/Checkbox';
import debounce from 'lodash.debounce';
import {Tooltip as ReactTooltip} from 'react-tooltip';


const useStyles = makeStyles({
  colorPrimary: {
    color: '#0087ff',
  },
});

const columnsWidthInitial = {};
export default function DataTable({
  data, columns, selectable, sortable, onSelect, onSort, fieldIcons, onColumnsChange, totalDuration, loading,
  lastPage, activePage, itemsCountPerPage, totalItemsCount, handlePagination, selectedItem, setSelectedItem, reports,
  footerButton, minHeight, tallRows,
  downloadExcel, downloadPdf, verticalOffset = '0px',
  columnsWidth = columnsWidthInitial, simpleTable, editRow,
  duplicateRow, timeOffRow, removeRow, multiselect = false, hoverActions = false, hoverable = false, id = 'first', grey, greyTitle,
  withoutFilterColumns = false,
  selectAllItems = null, colored = { warning: false, error: false },
  all = false, setAll = () => ({}), statusIcon = true,
  accountList = false,
  withoutShitCode,
  onEditBreak,
  onOpenAssignGroup,
  onOpenWorkTime,
  onEditAddress,
  onSearch = false,
  colSearch = {},
  openButton = false,
  onTableScroll,
  loadingSkeleton = false,
  loadingSkeletonRows = 8,
  showLoadingOverlay = true,
}) {
  const { t } = useTranslation();
  const [tableData, setTableData] = useState(data);
  const [, setAllSelected] = useState({ checked: 0, total: 0 });
  const [sortOptionsAsc, setSortOptionsAsc] = useState({});
  const [showSettingsPopup, setShowSettingsPopup] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [totalCustomWidthColumns, setTotalCustomWidthColumns] = useState(0);
  const [totalCustomColumns, setTotalCustomColumns] = useState(0);
  const [selectAll, setSelectAll] = useState(false);

  const classes = useStyles();

  const tableRef = useRef(null);

  useLayoutEffect(() => {
    if (typeof selectAllItems === 'function') {
      setSelectAll(true);
    }
  }, [selectAllItems]);

  useEffect(() => {
    const initSortOptions = (options) => {
      const arrayCopy = { ...options };
      for (let i = 0; i < columns.length; i += 1) {
        if (typeof arrayCopy[columns[i].field] === 'undefined') {
          arrayCopy[columns[i].field] = false;
        }
      }
      return arrayCopy;
    };
    setSortOptionsAsc(initSortOptions);
    setVisibleColumns(columns.filter((column) => column.checked));
    setTotalCustomWidthColumns(columns.reduce((total, column) => {
      if (column.checked && columnsWidth && columnsWidth[column.field]) {
        return total + columnsWidth[column.field];
      }
      return total;
    }, 0));
    setTotalCustomColumns(columns.reduce((total, column) => {
      if (column.checked && columnsWidth && columnsWidth[column.field]) {
        return total + 1;
      }
      return total;
    }, 0));
  }, [columns, columnsWidth]);

  useEffect(() => {
    const initData = { checked: 0, total: 0 };
    const checkAllSelected = () => {
      data.map((group) => {
        let checkedNumber = 0;
        for (let i = 0; i < group.items.length; i += 1) {
          if (group.items[i].checked) checkedNumber += 1;
        }
        if (checkedNumber === group.items.length) {
          initData.checked += 1;
          initData.total += 1;
        } else {
          initData.total += 1;
        }
        return group;
      });
      return initData;
    };
    if (data.length && data.items) setAllSelected(checkAllSelected);
    setTableData(data);
  }, [data]);

  useEffect(() => {
    document.documentElement.style.setProperty(`--scroll-left-${id}`, '0px');
  }, [id]);

  const sort = (field, asc, column) => {
    setSortOptionsAsc({ ...sortOptionsAsc, [field]: !asc });
    onSort(field, !asc, column);
  };

  const columnsChangeHandler = (item) => {
    const arrayCopy = [...columns];
    for (let i = 0; i < arrayCopy.length; i += 1) {
      if (arrayCopy[i].field === item.field) {
        arrayCopy[i].checked = !arrayCopy[i].checked;
      }
    }
    onColumnsChange(arrayCopy);
  };

  const footerTitleClasses = classNames(
    styles.footerTitle, styles.footerTitleReports,
  );

  const tableHeaderCell = classNames(
    styles.flexRowGlobal,
    styles.columnName,
    { [styles.flexRowGlobalReports]: reports },
  );

  const sortBlockClasses = classNames(
    styles.sortBlock,
    { [styles.pointer]: sortable },
  );

  const scrollableContentClasses = classNames(
    styles.scrollableContent,
    { [styles.scrollableContentWithoutRightPanel]: withoutFilterColumns },
    { [styles.scrollableContentReports]: reports },
    { [styles.scrollableContentMinHeight]: minHeight },
  );

  const flexTableClasses = classNames(
    styles.flexTable,
    styles.header,
    { [styles.flexTableHeaderReports]: reports },
    { [styles.flexTableHeaderWithSelectable]: selectable && !selectAll },
    // { [styles.flexTableHeaderReports]: reports, [styles.flexTableHeaderNotSelectable]: !reports && !selectable },
  );

  const tableContentClasses = classNames(
    styles.tableContent,
    {
      [styles.tableContentNotSortable]: !sortable,
      [styles.tableContentMinHeight]: minHeight,
    },
  );

  const tableContainerClasses = classNames(
    styles.tableContainer,
    {
      [styles.tableContainer_grey]: grey,
      [styles.tableContainer_greyTitle]: greyTitle,
      [styles.tableContainer_accountList]: accountList,
      [styles.tableContainer_simpleTable]: simpleTable,
      [styles.tableContainer_minHeight]: minHeight,
    },
  );

  const onScroll = useCallback(
    debounce((e) => {
      if (hoverActions) {
        if (e.scrollLeft <= e.contentScrollWidth - e.clientWidth) {
          document.documentElement.style.setProperty(`--scroll-left-${id}`, `${e.scrollLeft}px`);
        }
      }
      if (typeof onTableScroll === 'function') {
        onTableScroll(e);
      }
    }, 200), 
    [hoverActions, id, onTableScroll]
  );

  const shouldShowSkeletonRows = loadingSkeleton && loading && !tableData.length;

  return (
    <div
      className={tableContainerClasses}
      style={{ height: minHeight ? `auto` : `calc(100vh - ${verticalOffset})` }}
      role='table'
      aria-label='Destinations'
      data-id={id}
      ref={tableRef}
    >
      <Scrollbar
        className={scrollableContentClasses}
        style={{
          height: minHeight ? `auto` : `calc(100vh - ${verticalOffset} - ${simpleTable ? 0 : 47}px + ${accountList ? 47 : 0}px)`,
        }}
        removeTracksWhenNotUsed
        trackXProps={{
          renderer: ({ elementRef, ...restProps }) => (
            <span
              {...restProps}
              ref={elementRef}
              className={classNames(styles.scrollableContent__scrollbarTrackX, { trackX: true })}
            />
          ),
        }}
        trackYProps={{
          renderer: ({ elementRef, ...restProps }) => (
            <span
              {...restProps}
              ref={elementRef}
              className={classNames(styles.scrollableContent__scrollbarTrackY, { trackY: true })}
            />
          ),
        }}
        onScroll={onScroll}
      >
        <div className={tableContentClasses}>
          <div className={classNames({
            [styles.headerWrapper]: !simpleTable && !onSearch,
            [styles.simpleTable]: simpleTable,
            [styles.headerWrapperSearch]: onSearch,
          })}>
            <header className={flexTableClasses} role='rowgroup'>
              {
                visibleColumns.length > 0 && visibleColumns.map((column, idx) => {
                  let width = '';
                  let minWidth = column.minWidth || null;
                  if (totalCustomWidthColumns > 0) {
                    if (columnsWidth[column.field]) {
                      width = columnsWidth[column.field];
                      minWidth = columnsWidth[column.field];
                    } else {
                      width = selectable
                        ? `calc((100% - ${totalCustomWidthColumns + 40}px) / ${visibleColumns.length
                        - totalCustomColumns})`
                        : `calc((100% - ${totalCustomWidthColumns}px) / ${visibleColumns.length - totalCustomColumns})`;
                    }
                  } else {
                    width = selectable
                      ? `calc((100% - 40px) / ${visibleColumns.length})`
                      : `calc((100%) / ${visibleColumns.length})`;
                  }
                  return (
                    <div
                      key={idx.toString()}
                      className={tableHeaderCell}
                      style={{ width, minWidth }}
                      role='columnheader'
                    >
                      {
                       selectAll && idx === 0
                        && (
                        <StyledCheckbox
                          style={{ padding: '0 9px 0 3px' }}
                          checked={all}
                          onChange={() => {
                            setAll((prevState) => {
                              if (!prevState) {
                                selectAllItems(tableData);
                              } else {
                                selectAllItems([]);
                              }
                              return !prevState;
                            });
                          }}
                        />
                        )
                      }
                      <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                        className={sortBlockClasses}
                        onClick={() => sortable && sort(column.field, sortOptionsAsc[column.field], column)}
                      >
                        <div className={classNames(styles.flexCenter)}>
                          {typeof column.label === 'string' ? t(column.label) : column.label}
                        </div>
                        {
                          (fieldIcons && fieldIcons[column.field] && fieldIcons[column.field].length)
                          && fieldIcons[column.field].map((icon) => icon.icon)
                        }
                      </div>
                      { (onSearch && !column.disable_search && column.field !== 'status' && column.field !== 'is_kiosk' && column.field !== 'pin_code' && column.field !== 'rfid') &&
                        <div className={styles.headerSearch}>
                          <RowSearch
                            handleInputChange={(e) => { onSearch(column.field, e.target.value) }}
                            inputValues={colSearch[column.field] || ''}
                            title={t('Search')} />
                        </div>
                      }
                    </div>
                  );
                })
              }
            </header>
          </div>
          {
            tableData.length ? tableData.map((row, idx) => (
              <Row
                withoutRightPanel={withoutFilterColumns}
                index={idx}
                key={idx.toString()}
                row={row}
                tallRows={tallRows}
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                columns={visibleColumns}
                selectable={selectable}
                selectAll={selectAll}
                statysIcon={statusIcon}
                onSelect={onSelect}
                fieldIcons={fieldIcons}
                reports={reports}
                columnsWidth={columnsWidth}
                totalCustomWidthColumns={totalCustomWidthColumns}
                totalCustomColumns={totalCustomColumns}
                editRow={editRow}
                removeRow={removeRow}
                duplicateRow={duplicateRow}
                timeOffRow={timeOffRow}
                multiselect={multiselect}
                hoverActions={hoverActions}
                hoverable={hoverable}
                colored={colored}
                tableRef={tableRef}
                withoutShitCode={withoutShitCode}
                onEditBreak={onEditBreak}
                onEditAddress={onEditAddress}
                onOpenAssignGroup={onOpenAssignGroup}
                onOpenWorkTime={onOpenWorkTime}
                openButton={openButton}
              />
            )) : null
          }
          {
            shouldShowSkeletonRows
              ? Array.from({ length: loadingSkeletonRows }, (_, rowIdx) => (
                <div key={`skeleton-row-${rowIdx}`} className={classNames(styles.flexTable, styles.row)}>
                  {
                    visibleColumns.length > 0 && visibleColumns.map((column, colIdx) => {
                      let width = '';
                      let minWidth = column.minWidth || null;
                      if (totalCustomWidthColumns > 0) {
                        if (columnsWidth[column.field]) {
                          width = columnsWidth[column.field];
                          minWidth = columnsWidth[column.field];
                        } else {
                          width = selectable
                            ? `calc((100% - ${totalCustomWidthColumns + 40}px) / ${visibleColumns.length
                            - totalCustomColumns})`
                            : `calc((100% - ${totalCustomWidthColumns}px) / ${visibleColumns.length - totalCustomColumns})`;
                        }
                      } else {
                        width = selectable
                          ? `calc((100% - 40px) / ${visibleColumns.length})`
                          : `calc((100%) / ${visibleColumns.length})`;
                      }

                      const barWidth = `${Math.max(35, 85 - ((rowIdx + colIdx) % 4) * 12)}%`;

                      return (
                        <div
                          key={`skeleton-cell-${rowIdx}-${column.field}`}
                          className={classNames(styles.flexRowGroup, styles.skeletonCell)}
                          style={{ width, minWidth }}
                        >
                          {selectAll && colIdx === 0
                            ? <span className={styles.skeletonCheckbox} />
                            : <span className={styles.skeletonBar} style={{ width: barWidth }} />}
                        </div>
                      );
                    })
                  }
                </div>
              ))
              : null
          }
          {
            footerButton && (
              <div className={classNames(styles.footerButton)}>
                {footerButton}
              </div>
            )
          }
        </div>
      </Scrollbar>
      {!simpleTable
        && (
        <div className={classNames(styles.tableFooter)}>
          {typeof downloadExcel === 'function'
            && (
              <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                className={styles.pointer}
                onClick={downloadExcel}
              >
                <ExcelIcon />
              </div>
            )}
          {typeof downloadPdf === 'function'
            && (
              <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                className={styles.pointer}
                onClick={downloadPdf}
              >
                <PdfIcon />
              </div>
            )}
          {
            totalDuration && (
              <p className={footerTitleClasses}>
                {
                  t('Overall worktime')+': '
                }
                <span className={classNames(styles.totals, styles.blue, styles.bold)}>{totalDuration}</span>
              </p>
            )
          }
          {
            lastPage && lastPage > 1
              ? (
                <div className={classNames(styles.pagination)}>
                  <Pagination
                    activePage={activePage}
                    itemsCountPerPage={itemsCountPerPage}
                    totalItemsCount={totalItemsCount}
                    pageRangeDisplayed={3}
                    onChange={handlePagination}
                    itemClass={classNames(styles.paginationItem)}
                    linkClass={classNames(styles.paginationLink)}
                    activeLinkClass={classNames(styles.paginationLinkActive)}
                  />
                </div>
              )
              : null
          }
        </div>
        )}

      {
        !withoutFilterColumns && (
          <div className={classNames(simpleTable ? styles.scrollingSimplePanel : styles.scrollingPanel)}>
            {
              !reports && tableData.length > 0 && (
                <ClickAwayListener onClickAway={() => setShowSettingsPopup(false)}>
                  <aside
                    className={classNames(styles.columnName, styles.settingsCell)}
                    role='columnheader'
                  >
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                    <div onClick={() => setShowSettingsPopup(!showSettingsPopup)}>
                      <CogwheelIcon />
                    </div>
                    {
                      showSettingsPopup && (
                        <div className={styles.settingsPopup}>
                          <CheckboxGroupRaw items={columns.map((item) => { return {...item, label: typeof item.label === 'string' ? t(item.label) : item.label}})} onChange={columnsChangeHandler} />
                        </div>
                      )
                    }
                  </aside>
                </ClickAwayListener>
              )
            }
          </div>
        )
      }
      <div className={classNames(styles.overlay, { [styles.overlayActive]: loading && showLoadingOverlay })}>
        <CircularProgress classes={{ colorPrimary: classes.colorPrimary }} />
      </div>
      <ReactTooltip
        id='cell_description'
        className={styles.tooltip}
        effect='solid'
      />
      <ReactTooltip
        id='tooltip_button'
        className={styles.tooltip_black}
        effect='solid'
      />
    </div>
  );
}
